import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { WithStyles } from "@material-ui/core";
import { styles } from "./ManageUseCasesView.styles";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import {
    IUniversalTableColumn,
    IUniversalTableData,
} from "../../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { findProductByName } from "../../productOverview/ProductHelper";
import * as UseCasesAction from "../../../../stores/useCases/UseCasesAction";
import UseCaseCategoryModel from "../../../../models/useCase/UseCaseCategoryModel";
import UseCaseModel from "../../../../models/useCase/UseCaseModel";

export interface IManageUseCasesViewProps extends WithStyles<typeof styles> {}

const ManageUseCasesViewPure: React.FunctionComponent<IManageUseCasesViewProps> = ({
    classes
}: IManageUseCasesViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const useCasesFromState = useSelector(state => state.useCases.useCases);
    const categoriesFromState = useSelector(state => state.useCases.categories);
    const productsInfo = useSelector(state => state.products);

    let useCaseCategories: IUniversalTableData;
    if (categoriesFromState) {
        useCaseCategories = Object.values(categoriesFromState)?.map((category: UseCaseCategoryModel) => ({
            id: category.id,
            name: category.name,
            products: category.products?.map(p => findProductByName(productsInfo?.products, p))
        }));
    }

    let useCases: IUniversalTableData;
    if (useCasesFromState) {
        useCases = Object.values(useCasesFromState)?.map((useCase: UseCaseModel) => {
            return {
            id: useCase.id,
            name: useCase.name,
            description: useCase.description,
            link: useCase.link,
            categories: useCase.categories?.map(useCaseCategory => {
                const category = useCaseCategories?.find(category => category.id === useCaseCategory);
                return {
                    id: category?.id,
                    label: category?.name || "UNKNOWN"
                };
            })
        }});
    }
    
    const handleEditFinished = (index, newData) => {
        const updatedUseCase = {
            name: newData.name,
            description: newData.description,
            link: newData.link,
            categories: newData?.categories?.map(c => c.id)
        };
        dispatch(UseCasesAction.updateUseCase(useCases[index].id, updatedUseCase))
            .then(() => {
                dispatch(UseCasesAction.requestAllUseCases());
                enqueueSnackbar("Use case updated", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Use case update failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleDeleteFinished = index => {
        dispatch(UseCasesAction.deleteUseCase(useCases[index].id))
            .then(() => {
                dispatch(UseCasesAction.requestAllUseCases());
                enqueueSnackbar("Use case deleted", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Use case deletion failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleAddFinished = data => {
        const newUseCase = {
            name: data.name,
            description: data.description,
            link: data.link,
            categories: data?.categories?.map(c => c.id)
        };
        dispatch(UseCasesAction.addUseCase(newUseCase))
            .then(() => {
                dispatch(UseCasesAction.requestAllUseCases());
                enqueueSnackbar("Use case added", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Use case addition failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "multiline_text",
            noLines: 3,
            title: "Name",
            field: "name",
            validate: rowData => {
                return rowData?.name?.length > 0 ? true : { isValid: false, helperText: "Name cannot be empty" };
            }
        },
        {
            type: "multiselect",
            title: "Categories",
            field: "categories",
            allValues: useCaseCategories?.map(c => ({ id: c.id, label: c.name })),
            validate: rowData => {
                return rowData?.categories?.length > 0 ? true : { isValid: false, helperText: "At least one category must be selected" };
            }
        },
        {
            type: "multiline_text",
            title: "Description",
            field: "description",
            noLines: 10
        },
        {
            type: "link",
            title: "Link",
            linkField: "link",
            field: "link",
            openInNewTab: true,
            validate: rowData => {
                return rowData?.link?.length > 0 ? true : { isValid: false, helperText: "Link cannot be empty" };
            }
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEditFinished(index, newValue),
                    scope: "usecases.write"
                },
                {
                    name: "delete",
                    tooltip: t("general.delete"),
                    onDeleteFinished: index => handleDeleteFinished(index),
                    deleteQuestion: "Do you want to delete this use case?",
                    scope: "usecases.write"
                },
                {
                    name: "add",
                    tooltip: "Add a use case",
                    onAddFinished: newValue => handleAddFinished(newValue),
                    scope: "usecases.write"
                }
            ]
        }
    ];

    return (
        <div>
            <UniversalTable
                columns={columns}
                data={useCases}
                searchable
            />
        </div>
    );
};

export const ManageUseCasesView = withStyles(styles)(ManageUseCasesViewPure);
